import { FeedId } from 'features/streaming/types';
import { selectActiveVirtualBackground } from 'features/virtual-backgrounds/selectors';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { reportMediaCaptureFailure } from 'features/user-media/utils/reportMediaCaptureFailure';
import { stopStreamTracks } from 'features/user-media/utils';
import { store } from 'store/store';
import { noop } from 'utils/flow';
import { configureMediaTemplate, sendMessage } from 'utils/webrtc/messages';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { FeedMediaStates, FeedStreamInfo, PublishingKind, PublishOffer } from 'utils/webrtc/types';
import { ControlledPublishingHandle } from 'utils/webrtc/publishing/ControlledPublishingHandle';
import {
  CaptureSupersededPhase,
  emitPublishEvent,
  PUBLISH_CAPTURE_SUPERSEDED_EVENT,
  PUBLISH_RECONCILE_STALLED_EVENT,
  PUBLISH_RECONCILE_STALLED_MESSAGE,
  publishCaptureSupersededMessage,
} from 'utils/webrtc/publishing/publishTelemetry';

// Host members are read at call time, never captured: `ScreensharingFeed`'s field initializer
// replaces `mediaStates` after super() runs, and tests monkey-patch members post-construction.
export type VideoPublishHost = {
  handle?: ControlledPublishingHandle;
  kind: PublishingKind;
  readonly disposed: boolean;
  mediaStates: FeedMediaStates;
  signalingBus: {
    addMessage: (
      type: 'start' | 'stop',
      feedId: FeedId | undefined,
      streams: FeedStreamInfo[]
    ) => void;
  };
  mediaController: {
    requestExternalStream: (constraints?: MediaStreamConstraints) => Promise<MediaStream>;
  };
  getPeerConnection: () => RTCPeerConnection | undefined;
  requestPublish: (offerParams?: PublishOffer) => Promise<void>;
  getActiveMediaConfig: () => {
    video?: boolean | MediaTrackConstraints;
    audio?: boolean | MediaTrackConstraints;
    stream?: MediaStream;
  };
  getVideoMid: () => string | undefined;
  hasVideoTransceiver: () => boolean;
  activateVirtualBackground: (
    config: SelectedVirtualBackground,
    params?: { stream?: MediaStream }
  ) => Promise<MediaStream | undefined>;
  updateRedux: () => void;
};

// Backstop against an unbounded spin if intent flip-flops mid-apply; never hit in practice
// since a burst coalesces to ~1 real op.
const VIDEO_RECONCILE_MAX_STEPS = 8;

export class VideoBroadcastReconciler {
  // Public: the seam suite seeds and inspects these directly.
  // Capture-epoch token, bumped on every mute/unmute so an in-flight unmute capture can detect
  // it was superseded and drop its stale track (a boolean can't tell "re-muted" from "newer won").
  videoCaptureGeneration = 0;

  // Reality signal for the reconcile chain: whether a camera video publish is currently in
  // effect. Distinct from `mediaStates.video.broadcasting`, which is now purely user intent.
  videoBroadcastActive = false;

  videoReconcileChain: Promise<void> = Promise.resolve();

  // Denominator for the stall/superseded telemetry rate. An instance field, not part of
  // negotiationMeta: that object is replaced wholesale by reconnectionReset, which would
  // silently zero this out on every reconnect.
  private reconcileCount = 0;

  constructor(private host: VideoPublishHost) {}

  // SFU confirmed a live video sender: latch reality from the current user intent.
  latchFromIntent = () => {
    this.videoBroadcastActive = this.host.mediaStates.video.broadcasting;
  };

  // Keep the video-live latch in lockstep with mediaStates.video.broadcasting so a teardown
  // can't leave it stale and short-circuit the next grant's reconcile.
  resetLatch = () => {
    this.videoBroadcastActive = false;
  };

  // The old PC is gone: a chain suspended in a never-settling apply must not wedge future
  // toggles, and reality is false on the fresh PC by definition.
  resetForReconnection = () => {
    this.videoReconcileChain = Promise.resolve();
    this.videoBroadcastActive = false;
  };

  // Serializes camera publish mutations through one promise chain; the chain continuation is
  // kept rejection-free (`.catch`) so a throwing op doesn't deadlock later toggles.
  enqueueVideoReconcile = (): Promise<void> => {
    const run = this.videoReconcileChain.then(() => this.reconcileVideoBroadcast());
    this.videoReconcileChain = run.catch(() => undefined);

    return run;
  };

  private reconcileVideoBroadcast = async (): Promise<void> => {
    this.reconcileCount += 1;

    // Sequential awaits are the point: this loop converges intent → reality one step at a time,
    // and a step must fully settle before the next re-reads intent.
    /* eslint-disable no-await-in-loop */
    for (let step = 0; step < VIDEO_RECONCILE_MAX_STEPS; step += 1) {
      if (this.host.disposed || !this.host.handle) {
        return;
      }
      const intent = this.host.mediaStates.video.broadcasting;
      if (intent === this.videoBroadcastActive) {
        return;
      }
      if (intent) {
        await this.activateVideoBroadcast();
      } else {
        await this.deactivateVideoBroadcast();
      }
    }
    /* eslint-enable no-await-in-loop */

    emitPublishEvent({
      event: PUBLISH_RECONCILE_STALLED_EVENT,
      message: PUBLISH_RECONCILE_STALLED_MESSAGE,
      level: 'warn',
      feedType: this.host.kind,
      visibility: 'fleet',
      cap: 3,
      attributes: {
        videoIntended: this.host.mediaStates.video.broadcasting,
        videoActive: this.videoBroadcastActive,
        steps: VIDEO_RECONCILE_MAX_STEPS,
        reconcileCount: this.reconcileCount,
        hasHandle: !!this.host.handle,
      },
    });
  };

  // A VB restore inside prepareOffer opens the camera seconds before the offer exists, so intent
  // may already be OFF. Not awaited: the commit itself may be running inside the reconcile chain.
  commitVideoBroadcast = (commitsVideo: boolean) => {
    if (!commitsVideo) {
      return;
    }

    this.videoBroadcastActive = true;

    if (!this.host.mediaStates.video.broadcasting) {
      this.enqueueVideoReconcile().catch(noop);
    }
  };

  activateVideoBroadcast = async () => {
    if (this.host.hasVideoTransceiver()) {
      // warm: renegotiation-free replace (handles VB capture + async-capture guard); unmute may
      // roll broadcasting back to OFF on denied capture, so mirror that outcome for the converge loop.
      await this.unmuteVideoTrack();
      this.videoBroadcastActive = this.host.mediaStates.video.broadcasting;
      return;
    }

    // Camera was off (no transceiver yet): addVideo initialises it; replaceVideo would be a no-op.
    // requestPublish latches reality once the offer is issued, not once janus's callbacks settle — a synchronous throw leaves reality false and retryable.
    const selectedVB = selectActiveVirtualBackground(store.getState());
    if (selectedVB.value) {
      const streamVB = await this.host.activateVirtualBackground(selectedVB);
      if (streamVB) {
        await this.host.requestPublish({ stream: streamVB, intent: { video: 'send' } });
        return;
      }
    }

    await this.host.requestPublish({ intent: { video: 'send' } });
  };

  deactivateVideoBroadcast = async () => {
    this.videoBroadcastActive = false;

    if (this.host.kind === 'publishing') {
      await VirtualBackgroundManager.deactivate();
    }

    if (this.host.handle && this.host.kind === 'publishing') {
      const videoStreams = Object.values(this.host.handle.streams).filter(
        (stream) => stream.type === 'video'
      );
      // Map is empty until the first media event. A stop is safe to send early where a start is not
      // (unmuteVideoTrack): an unknown mid is a no-op for peers, and the send is unacknowledged.
      const fallbackMid = videoStreams.length ? undefined : this.host.getVideoMid();
      const stopped: FeedStreamInfo[] = fallbackMid
        ? [{ type: 'video', mid: fallbackMid }]
        : videoStreams;

      if (stopped.length) {
        this.host.signalingBus.addMessage('stop', this.host.handle.feedId, stopped);
      }
    }

    // Skip for screenshare: stopScreenshare already removed the video transceiver, so muting
    // would fire replaceTracks at a torn-down mid (the live-track guard no-ops it anyway).
    if (this.host.kind !== 'screensharing') {
      await this.muteVideoTrack();
    }
  };

  // Renegotiation-free swap via the tracks API: no `capture` ⇒ mute (replaceTrack(null)), a track
  // ⇒ unmute. A no-capture replace stages the transceiver recvonly — forceVideoSendonly fixes it.
  replaceVideoTrack = (track: MediaStreamTrack | null, mid: string | undefined): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      if (!this.host.handle || !mid) {
        resolve();
        return;
      }
      this.host.handle.videoroom.replaceTracks({
        tracks: [{ type: 'video', mid, ...(track ? { capture: track } : {}) }],
        success: () => {
          this.forceVideoSendonly();
          resolve();
        },
        error: (e: unknown) => reject(e),
      });
    });

  // A no-capture swap stages the transceiver recvonly → the next renegotiation tells the SFU we're
  // non-sending. Re-assert sendonly; find it by receiver.track (survives replaceTrack(null)).
  private forceVideoSendonly = () => {
    const transceiver = this.host
      .getPeerConnection()
      ?.getTransceivers()
      .find((t) => t.receiver?.track?.kind === 'video');
    if (!transceiver) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tc = transceiver as any;
    if (tc.setDirection) {
      tc.setDirection('sendonly');
    } else {
      tc.direction = 'sendonly';
    }
  };

  // Tell the videoroom plugin video is (in)active via configure, without a jsep.
  signalVideoBroadcast = (on: boolean) => {
    if (!this.host.handle) {
      return;
    }
    sendMessage(this.host.handle.videoroom, configureMediaTemplate({ video: on })).catch(noop);
  };

  // Mute = replaceTrack(null) + stop the outgoing track (janus' own stop is unreliable for app/VB
  // streams) + configure {video:false}. Gated on a live sender, so it no-ops when cold/torn-down.
  muteVideoTrack = async (mid: string | undefined = this.host.getVideoMid()) => {
    // Bump first: any mute intent invalidates an in-flight unmute, even if mute no-ops below.
    this.videoCaptureGeneration += 1;
    if (!this.host.handle || !mid) {
      return;
    }
    const sender = this.host
      .getPeerConnection()
      ?.getTransceivers()
      .find((transceiver) => transceiver.sender?.track?.kind === 'video')?.sender;
    const outgoingTrack = sender?.track ?? null;
    if (!outgoingTrack) {
      return;
    }

    await this.replaceVideoTrack(null, mid);
    outgoingTrack.stop();
    this.signalVideoBroadcast(false);
  };

  // Stream to publish on unmute: VB-processed if a background is active, else a fresh camera
  // capture. undefined on capture failure (gUM denied / VB failed).
  private captureVideoForUnmute = async (): Promise<MediaStream | undefined> => {
    const selectedVB = selectActiveVirtualBackground(store.getState());
    if (selectedVB.value) {
      const streamVB = await this.host.activateVirtualBackground(selectedVB);
      return streamVB;
    }
    return this.host.mediaController.requestExternalStream({
      video: this.host.getActiveMediaConfig().video,
    });
  };

  // Unmute = fresh capture + replaceTrack(track). After the async capture, guard in order:
  // (1) epoch superseded, (2) user re-muted, (3) capture failed → roll broadcasting back.
  unmuteVideoTrack = async () => {
    this.videoCaptureGeneration += 1;
    const generation = this.videoCaptureGeneration;
    let stream: MediaStream | undefined;
    let captureError: unknown;
    try {
      stream = await this.captureVideoForUnmute();
    } catch (error) {
      captureError = error;
    }
    let track = stream?.getVideoTracks()[0];

    // Discard only when intent dropped (re-muted mid-capture); generation is observe-only now
    // that the reconcile chain serializes camera ops, so a move means a caller escaped it.
    this.warnIfVideoCaptureSuperseded(generation, 'capture');
    if (!this.host.mediaStates.video.broadcasting) {
      await this.discardUnmuteCapture(stream);
      return;
    }

    // A stuck VB `_activated` latch short-circuits later activates, so capture comes back empty
    // though a background is still selected. Reconcile once, re-derive, fail closed.
    if (
      !track &&
      VirtualBackgroundManager.activated &&
      selectActiveVirtualBackground(store.getState()).value
    ) {
      await VirtualBackgroundManager.deactivate();
      try {
        stream = await this.captureVideoForUnmute();
      } catch (error) {
        captureError = error;
      }
      track = stream?.getVideoTracks()[0];

      // The re-derive opened a second async window; honor a mute that landed in it.
      this.warnIfVideoCaptureSuperseded(generation, 'rederive');
      if (!this.host.mediaStates.video.broadcasting) {
        await this.discardUnmuteCapture(stream);
        return;
      }
    }

    // (3) capture failed while still current: roll back the optimistic broadcasting=true and report
    // a real denial (else the slice stays 'granted' and the toggle's error UI never shows).
    if (!track) {
      reportMediaCaptureFailure('videoinput', captureError);
      this.host.mediaStates.video.enabled = false;
      this.host.mediaStates.video.broadcasting = false;
      this.host.mediaStates.video.captured = false;
      this.host.updateRedux();
      return;
    }

    const mid = this.host.getVideoMid();
    await this.replaceVideoTrack(track, mid);

    // A mute during this await can't stop the track (not on the sender yet → muteVideoTrack
    // no-ops), so release it here — discardUnmuteCapture also unlatches a virtual background.
    this.warnIfVideoCaptureSuperseded(generation, 'replace');
    if (!this.host.mediaStates.video.broadcasting) {
      await this.replaceVideoTrack(null, mid);
      await this.discardUnmuteCapture(stream);
      return;
    }

    this.signalVideoBroadcast(true);

    // Firefox fires no SFU media event on a renegotiation-free unmute, so announce directly —
    // but only for SFU-confirmed m-lines: announcing before configure-ok 428s subscribers.
    if (this.host.handle && mid && this.host.handle.streams?.[mid]) {
      this.host.signalingBus.addMessage('start', this.host.handle.feedId, [{ type: 'video', mid }]);
      this.host.mediaStates.video.captured = true;
    }
  };

  // Observe-only supersession alarm, kept until a clean prod bake proves overlapping camera ops
  // are unreachable; a hit means a caller mutated the camera outside the reconcile chain.
  private warnIfVideoCaptureSuperseded = (generation: number, phase: CaptureSupersededPhase) => {
    if (generation === this.videoCaptureGeneration) {
      return;
    }
    emitPublishEvent({
      event: PUBLISH_CAPTURE_SUPERSEDED_EVENT,
      message: publishCaptureSupersededMessage(phase),
      level: 'warn',
      feedType: this.host.kind,
      visibility: 'internal',
      cap: 5,
      attributes: {
        capturePhase: phase,
        capturedGeneration: generation,
        currentGeneration: this.videoCaptureGeneration,
        reconcileCount: this.reconcileCount,
      },
    });
  };

  // A discarded capture can leave the VB manager latched on a live cloned track (LED on); release
  // it now. The `!broadcasting` gate keeps a newer still-broadcasting op's manager intact.
  private discardUnmuteCapture = async (stream: MediaStream | undefined): Promise<void> => {
    if (stream) {
      stopStreamTracks(stream);
    }
    if (!this.host.mediaStates.video.broadcasting && VirtualBackgroundManager.activated) {
      await VirtualBackgroundManager.deactivate();
    }
  };
}
